import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { CATEGORIES, ZONES } from "../data/ideas";

const IMG_POSITIONS = ["center", "top", "50% 30%", "50% 70%", "left center", "right center", "center bottom"];

function IdeaCard({ idea, index, onOpen }) {
  const cat = CATEGORIES.find((c) => c.key === idea.cat);
  const zone = ZONES.find((z) => z.key === idea.zona);
  return (
    <motion.button
      layout
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (index % 6) * 0.04 }}
      onClick={() => onOpen(idea)}
      data-testid={`idea-card-${idea.n}`}
      className="group text-left focus:outline-none"
    >
      <div className="relative aspect-square overflow-hidden rounded-lg bg-[#0d2c54]">
        <img
          src={cat?.image}
          alt={idea.t}
          loading="lazy"
          style={{ objectPosition: IMG_POSITIONS[idea.n % IMG_POSITIONS.length] }}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0d14]/80 via-[#0a0d14]/15 to-[#0a0d14]/35" />
        <span className="absolute top-3 left-3 font-display font-black text-3xl md:text-4xl leading-none text-white">
          {String(idea.n).padStart(3, "0")}
        </span>
        <span className="absolute bottom-3 left-3 bg-[#d9381e] text-white font-mono-num uppercase text-[9px] tracking-widest px-1.5 py-1">
          {cat?.short}
        </span>
        <span className="absolute right-3 top-3 h-8 w-8 flex items-center justify-center bg-[#d9381e] opacity-0 group-hover:opacity-100 transition-opacity">
          <ArrowUpRight className="h-4 w-4 text-white" />
        </span>
      </div>
      <h3 className="mt-3 font-display font-bold text-base md:text-lg text-[#0a0d14] leading-tight group-hover:text-[#0d2c54] transition-colors">
        {idea.t}
      </h3>
      <p className="mt-1 font-mono-num text-xs md:text-sm text-[#0a0d14]/55">
        {cat?.label} · {zone?.short}
      </p>
    </motion.button>
  );
}

function IdeaModal({ idea, onClose }) {
  const cat = idea ? CATEGORIES.find((c) => c.key === idea.cat) : null;
  const zone = idea ? ZONES.find((z) => z.key === idea.zona) : null;
  return (
    <AnimatePresence>
      {idea && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="idea-modal-overlay"
          className="fixed inset-0 z-[70] bg-[#0a0d14]/80 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-testid="idea-modal"
            className="relative w-full max-w-2xl bg-[#0d2c54] text-white overflow-hidden"
          >
            {cat && (
              <div className="relative h-40 md:h-52 overflow-hidden">
                <img src={cat.image} alt={cat.label} className="h-full w-full object-cover opacity-40" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d2c54] to-transparent" />
                <span className="absolute top-4 left-6 font-mono-num text-6xl md:text-7xl font-black text-[#d9381e] leading-none">
                  {String(idea.n).padStart(3, "0")}
                </span>
              </div>
            )}
            <button
              onClick={onClose}
              data-testid="idea-modal-close"
              className="absolute top-4 right-4 h-10 w-10 flex items-center justify-center bg-[#0a0d14]/40 hover:bg-[#d9381e] transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="p-6 md:p-10">
              <span className="font-mono-num uppercase text-[11px] tracking-widest text-white/50">
                {cat?.label} · {zone?.label}
              </span>
              <h3 className="mt-3 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl leading-none">
                {idea.t}
              </h3>
              <p className="mt-5 text-white/80 text-base md:text-lg leading-relaxed">
                {idea.d}
              </p>
              <div className="mt-8 flex items-center gap-3 border-t border-white/15 pt-5">
                <span className="h-2 w-2 bg-[#d9381e]" />
                <span className="font-mono-num uppercase text-[11px] tracking-widest text-white/50">
                  Idea {String(idea.n).padStart(3, "0")} / 100 · 100 Idee per Verona
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Ideas({ ideas }) {
  const [active, setActive] = useState("tutte");
  const [zona, setZona] = useState("all");
  const [selected, setSelected] = useState(null);
  const [limit, setLimit] = useState(8);

  useEffect(() => {
    setLimit(8);
  }, [active, zona]);

  const filtered = useMemo(() => {
    return ideas.filter((i) => {
      const okCat = active === "tutte" || i.cat === active;
      const okZona = zona === "all" || i.zona === zona;
      return okCat && okZona;
    });
  }, [active, zona, ideas]);

  const displayed = filtered.slice(0, limit);

  const tabs = [{ key: "tutte", short: "Tutte" }, ...CATEGORIES];
  const zoneTabs = [{ key: "all", short: "Tutte le zone" }, ...ZONES];

  return (
    <section id="idee" data-testid="ideas-section" className="py-20 md:py-28">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="flex items-center gap-3 mb-4">
              <span className="h-2 w-2 bg-[#d9381e]" />
              <span className="font-mono-num uppercase text-xs tracking-[0.25em] text-[#0a0d14]/60">
                Il Programma
              </span>
            </span>
            <h2 className="font-display font-black uppercase text-[#0a0d14] tracking-tighter text-5xl md:text-6xl lg:text-7xl leading-[0.85]">
              Cento idee,
              <br />
              <span className="text-[#0d2c54]">zero slogan.</span>
            </h2>
          </div>
          <p className="max-w-sm text-[#0a0d14]/70 leading-relaxed font-medium">
            Ogni proposta è numerata e divisa per tema. Filtra per l'argomento
            che ti sta più a cuore e clicca su un'idea per leggerla nel dettaglio.
          </p>
        </div>

        <div id="temi" className="mb-10 scroll-mt-24 space-y-5">
          <div>
            <span className="block mb-2 font-mono-num uppercase text-[10px] tracking-widest text-[#0a0d14]/45">
              Filtra per tema
            </span>
            <div data-testid="filter-tabs" className="flex flex-nowrap overflow-x-auto md:flex-wrap gap-0 border border-[#0a0d14]/15 no-scrollbar">
              {tabs.map((t) => {
                const isActive = active === t.key;
                const count = ideas.filter(
                  (i) => (t.key === "tutte" || i.cat === t.key) && (zona === "all" || i.zona === zona)
                ).length;
                return (
                  <button
                    key={t.key}
                    data-testid={`filter-${t.key}`}
                    onClick={() => setActive(t.key)}
                    className={`flex shrink-0 items-center gap-2 px-4 md:px-5 py-3 font-mono-num uppercase text-[11px] md:text-xs tracking-widest border-r border-b border-[#0a0d14]/12 transition-colors ${
                      isActive ? "bg-[#0d2c54] text-white" : "bg-transparent text-[#0a0d14]/70 hover:bg-[#e5e5e2]"
                    }`}
                  >
                    {t.short}
                    <span className={`text-[9px] ${isActive ? "text-[#d9381e]" : "text-[#0a0d14]/40"}`}>
                      {String(count).padStart(2, "0")}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <span className="block mb-2 font-mono-num uppercase text-[10px] tracking-widest text-[#0a0d14]/45">
              Filtra per circoscrizione
            </span>
            <div data-testid="zone-filter-tabs" className="flex flex-nowrap overflow-x-auto md:flex-wrap gap-0 border border-[#0a0d14]/15 no-scrollbar">
              {zoneTabs.map((z) => {
                const isActive = zona === z.key;
                const count = ideas.filter(
                  (i) => (z.key === "all" || i.zona === z.key) && (active === "tutte" || i.cat === active)
                ).length;
                return (
                  <button
                    key={z.key}
                    data-testid={`zone-${z.key}`}
                    onClick={() => setZona(z.key)}
                    className={`flex shrink-0 items-center gap-2 px-4 py-3 font-mono-num uppercase text-[11px] tracking-widest border-r border-b border-[#0a0d14]/12 transition-colors ${
                      isActive ? "bg-[#d9381e] text-white" : "bg-transparent text-[#0a0d14]/70 hover:bg-[#e5e5e2]"
                    }`}
                  >
                    {z.short}
                    <span className={`text-[9px] ${isActive ? "text-white/70" : "text-[#0a0d14]/40"}`}>
                      {String(count).padStart(2, "0")}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <motion.div
          layout
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-8"
        >
          <AnimatePresence mode="popLayout">
            {displayed.map((idea, i) => (
              <IdeaCard key={idea.id || idea.n} idea={idea} index={i} onOpen={setSelected} />
            ))}
          </AnimatePresence>
        </motion.div>

        {filtered.length > 8 && (
          <div className="mt-8 flex flex-col items-center gap-4">
            <span data-testid="ideas-count" className="font-mono-num uppercase text-[11px] tracking-widest text-[#0a0d14]/50">
              Mostrate {displayed.length} di {filtered.length} idee
            </span>
            {displayed.length < filtered.length ? (
              <div className="w-full flex flex-col items-center gap-3">
                <button
                  data-testid="load-more-btn"
                  onClick={() => setLimit((n) => n + 8)}
                  className="w-full sm:w-auto sm:px-20 bg-[#0d2c54] text-white py-4 font-display font-bold uppercase tracking-wide hover:bg-[#d9381e] transition-colors"
                >
                  Mostra altre idee ({filtered.length - displayed.length})
                </button>
                <button
                  data-testid="show-all-btn"
                  onClick={() => setLimit(filtered.length)}
                  className="font-mono-num uppercase text-[11px] tracking-widest text-[#0a0d14]/50 hover:text-[#d9381e] transition-colors"
                >
                  Mostra tutte
                </button>
              </div>
            ) : (
              <button
                data-testid="show-less-btn"
                onClick={() => {
                  setLimit(8);
                  document.getElementById("idee")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="font-mono-num uppercase text-[11px] tracking-widest text-[#0a0d14]/50 hover:text-[#d9381e] transition-colors"
              >
                Mostra meno
              </button>
            )}
          </div>
        )}
      </div>

      <IdeaModal idea={selected} onClose={() => setSelected(null)} />
    </section>
  );
}
